import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Gear, Check } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

const CookieBanner = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem('cookie-consent');
    if (!savedConsent) {
      setIsVisible(true);
    } else {
      const parsedConsent = JSON.parse(savedConsent);
      setConsent(parsedConsent);
      applyConsent(parsedConsent);
    }

    // Add global function to check cookie settings (for debugging)
    window.checkCookieSettings = () => {
      const consent = localStorage.getItem('cookie-consent');
      console.log('Current cookie settings:', consent ? JSON.parse(consent) : 'No consent given');
      return consent ? JSON.parse(consent) : null;
    };
  }, []);

  const applyConsent = (consentData) => {
    console.log('Applying cookie consent:', consentData);
    
    // Apply consent to tracking scripts
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': consentData.analytics ? 'granted' : 'denied',
        'ad_storage': consentData.marketing ? 'granted' : 'denied',
        'functionality_storage': consentData.preferences ? 'granted' : 'denied'
      });
      console.log('Google Analytics consent updated');
    }

    // Handle Hotjar
    if (window.hj) {
      if (consentData.analytics) {
        // Enable Hotjar
        window.hj('opt_in');
        console.log('Hotjar enabled');
      } else {
        // Disable Hotjar
        window.hj('opt_out');
        console.log('Hotjar disabled');
      }
    }

    // Handle ReactGA
    if (window.gtag && consentData.analytics) {
      // Re-initialize ReactGA with consent
      if (typeof window.ReactGA !== 'undefined') {
        window.ReactGA.initialize('G-J7QBYMSK3N', {
          gtagOptions: {
            'analytics_storage': 'granted'
          }
        });
        console.log('ReactGA re-initialized with consent');
      }
    }
  };

  const handleAcceptAll = () => {
    const newConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setConsent(newConsent);
    localStorage.setItem('cookie-consent', JSON.stringify(newConsent));
    applyConsent(newConsent);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const newConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setConsent(newConsent);
    localStorage.setItem('cookie-consent', JSON.stringify(newConsent));
    applyConsent(newConsent);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    applyConsent(consent);
    setIsVisible(false);
  };

  const toggleConsent = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    setConsent(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 lg:py-6">
          {!showSettings ? (
            // Main banner view
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                    <Cookie size={20} className="ph-thin text-[#0D2B45] sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2">
                      {t('cookieBanner.title')}
                    </h3>
                    <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                      {t('cookieBanner.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col gap-2 sm:gap-3">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={handleAcceptAll}
                      className="px-4 py-2.5 sm:px-6 sm:py-3 bg-[#0D2B45] text-white hover:bg-[#0D2B45]/90 rounded-lg font-medium transition-colors text-xs sm:text-sm flex-1 sm:flex-none"
                    >
                      {t('cookieBanner.acceptAll')}
                    </button>
                    <button
                      onClick={handleRejectAll}
                      className="px-4 py-2.5 sm:px-6 sm:py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors text-xs sm:text-sm flex-1 sm:flex-none"
                    >
                      {t('cookieBanner.rejectAll')}
                    </button>
                  </div>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="text-[#0D2B45] text-xs sm:text-sm font-medium hover:underline flex items-center gap-1 py-1.5 sm:py-2 self-start"
                  >
                    <Gear size={14} className="ph-thin sm:w-4 sm:h-4" />
                    {t('cookieBanner.customizeSettings')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // Settings view
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Gear size={20} className="ph-thin text-[#0D2B45]" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('cookieBanner.settings.title')}
                  </h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={18} className="ph-thin text-gray-500" />
                </button>
              </div>

              <div className="space-y-3">
                {/* Necessary Cookies */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                        {t('cookieBanner.settings.necessary.title')}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {t('cookieBanner.settings.necessary.description')}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-3">
                      <Check size={16} className="ph-thin text-green-600" />
                      <span className="text-xs text-green-600 font-medium">
                        {t('cookieBanner.settings.alwaysActive')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-white border border-gray-200 p-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                        {t('cookieBanner.settings.analytics.title')}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {t('cookieBanner.settings.analytics.description')}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleConsent('analytics')}
                      className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ml-3 p-1 ${
                        consent.analytics ? 'bg-[#0D2B45]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                          consent.analytics ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-white border border-gray-200 p-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                        {t('cookieBanner.settings.marketing.title')}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {t('cookieBanner.settings.marketing.description')}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleConsent('marketing')}
                      className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ml-3 p-1 ${
                        consent.marketing ? 'bg-[#0D2B45]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                          consent.marketing ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Preferences Cookies */}
                <div className="bg-white border border-gray-200 p-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                        {t('cookieBanner.settings.preferences.title')}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {t('cookieBanner.settings.preferences.description')}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleConsent('preferences')}
                      className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ml-3 p-1 ${
                        consent.preferences ? 'bg-[#0D2B45]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                          consent.preferences ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={handleAcceptAll}
                    className="px-4 py-2.5 bg-[#0D2B45] text-white hover:bg-[#0D2B45]/90 rounded-lg font-medium transition-colors text-sm flex-1"
                  >
                    {t('cookieBanner.acceptAll')}
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-4 py-2.5 bg-gray-600 text-white hover:bg-gray-700 rounded-lg font-medium transition-colors text-sm flex-1"
                  >
                    {t('cookieBanner.savePreferences')}
                  </button>
                </div>
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors text-sm"
                >
                  {t('cookieBanner.rejectAll')}
                </button>
              </div>

              <div className="mt-3 text-center">
                <p className="text-xs text-gray-500">
                  {t('cookieBanner.privacyNotice')}{' '}
                  <a href="/privacy-policy" className="text-[#0D2B45] hover:underline">
                    {t('cookieBanner.privacyPolicy')}
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
